import { DestroyRef, Directive, ElementRef, afterNextRender, inject, output } from '@angular/core';

// How far outside the visible area an element counts as in view, so what follows it can load just before it shows.
const IN_VIEW_MARGIN = '200px';

// The nearest ancestor that scrolls: an element is clipped by it, so the margin has to extend it, not the window.
function scrollingAncestorOf(element: HTMLElement): HTMLElement | null {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') return parent;
  }
  return null;
}

// Emits when its element comes into view, or near it, within whatever scrolls it.
@Directive({
  selector: '[nspInView]',
})
export class InViewDirective {
  readonly nspInView = output<void>();

  constructor() {
    const element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    const destroyRef = inject(DestroyRef);
    // Once rendered, so the element is in the page and its scrolling ancestor can be found.
    afterNextRender(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.nspInView.emit();
        },
        { root: scrollingAncestorOf(element), rootMargin: IN_VIEW_MARGIN }
      );
      observer.observe(element);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }
}
